import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type KillSwitchView, type PricingAutomationDecisionView } from '@vello/api-client';
import { Badge, Button, EmptyState, ListTable, Notice, PageHeader, Panel, StatCard, type Column, SectionTitle, Text, Row, AUTO_REFRESH_MS, useAutoRefresh } from '@vello/web-theme';
import {
  PRICING_GLOBAL_SCOPE,
  formatMarketInstant,
  pricingKillSwitchTone,
  marketTimeZone,
  mayFlipPricingKillSwitch,
  multiplierFromBasisPoints,
  pricingDecisionTone,
  sortPricingDecisions,
  unsetPricingKillSwitch,
} from '../../src/index';
import { useAdmin } from '../session';
import { ScopeMarketNotice, useScopeMarket } from '../components/scope-market';
import { useConsole } from '../console';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';

/**
 * The kill switch the header scope names (owner, 2026-10-12: no screen-level picker). Global → the platform switch; a
 * city (or the only market the grants reach) → that market's switch; a country with several markets → "pick a city":
 * a country scope must NEVER read or flip the platform switch, which governs every country.
 */
export function DynamicPricingScreen(): ReactElement {
  const { scope } = useAdmin();
  const { t } = useT();
  const { market, choices } = useScopeMarket();
  const global = scope.current.country === undefined && scope.current.city === undefined;
  const pricingScope = scope.current.city ?? (global ? PRICING_GLOBAL_SCOPE : market);
  if (pricingScope === undefined) {
    return (
      <>
        <PageHeader title={t('dynp.title')} subtitle={t('dynp.subtitle')} />
        <ScopeMarketNotice choices={choices} />
      </>
    );
  }
  return <DynamicPricingPanel pricingScope={pricingScope} />;
}

function DynamicPricingPanel({ pricingScope }: { readonly pricingScope: string }): ReactElement {
  const { runtime, markets, grants } = useAdmin();
  const { toast, confirm } = useConsole();
  const { t, lang } = useT();
  const { fail, v } = useVocab();
  const mayFlip = mayFlipPricingKillSwitch(grants);

  // Review W6N-03: the scope it reads and WRITES comes from the console scope on every render, so switching the
  // scope A→B can never leave the kill switch aimed at A.
  const currentScope = useRef(pricingScope);
  currentScope.current = pricingScope;
  const timeZone = marketTimeZone(pricingScope, markets);

  // `unset` (review W6N-02): the switch was never written — the GET answers 404 and the decision route treats a
  // missing switch as NOT killed, so the screen shows it live at revision 0 and lets the operator write the first row.
  const [killSwitch, setKillSwitch] = useState<{ readonly view: KillSwitchView; readonly unset: boolean } | undefined>(undefined);
  const [switchUnreadable, setSwitchUnreadable] = useState(false);
  const [decisions, setDecisions] = useState<readonly PricingAutomationDecisionView[] | undefined>(undefined);
  const [logError, setLogError] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const readSeq = useRef(0);

  const refused = useCallback(
    (e: { readonly code: string; readonly reasonCode?: string }): string => (e.code === 'FORBIDDEN' ? t('w6.refused.forbidden') : t('w6.refused', { code: fail(e) })),
    [t],
  );

  const load = useCallback(async (): Promise<void> => {
    const seq = ++readSeq.current;
    const [switchRes, logRes] = await Promise.all([runtime.pricingAutomation.getKillSwitch(pricingScope), runtime.pricingAutomation.listDecisions(pricingScope)]);
    if (seq !== readSeq.current) return; // a scope switch overtook this read
    if (switchRes.ok) {
      setKillSwitch({ view: switchRes.value.killSwitch, unset: switchRes.value.unset === true });
      setSwitchUnreadable(false);
      setError(undefined);
    } else if (switchRes.error.code === 'NOT_FOUND') {
      setKillSwitch({ view: unsetPricingKillSwitch(pricingScope), unset: true });
      setSwitchUnreadable(false);
      setError(undefined);
    } else {
      setKillSwitch(undefined);
      setSwitchUnreadable(true);
      setError(refused(switchRes.error));
    }
    if (logRes.ok) {
      setDecisions(logRes.value.decisions);
      setLogError(undefined);
    } else {
      // A log that could not be read is not "no decision recorded".
      setDecisions(undefined);
      setLogError(refused(logRes.error));
    }
  }, [runtime, pricingScope, refused]);

  useEffect(() => {
    setKillSwitch(undefined);
    setSwitchUnreadable(false);
    setDecisions(undefined);
    setLogError(undefined);
    void load();
  }, [load]);
  useAutoRefresh(load, { intervalMs: AUTO_REFRESH_MS.live });

  const flip = async (): Promise<void> => {
    if (killSwitch === undefined) return;
    const target = pricingScope;
    const next = !killSwitch.view.killed;
    // No note field: `POST /v1/pricing-automation/kill-switch` takes `{ scope, killed }` only, so a reason typed here
    // would be dropped on the floor while the operator believed it was on the record (review W6N-10).
    const answer = await confirm({
      title: t(next ? 'dynp.kill.title' : 'dynp.revive.title'),
      body: t(next ? 'dynp.kill.body' : 'dynp.revive.body'),
      confirmLabel: t(next ? 'dynp.kill.confirm' : 'dynp.revive.confirm'),
      cancelLabel: t('ui.cancel'),
      tone: next ? 'danger' : 'primary',
      icon: next ? 'ban' : 'rotateCcw',
    });
    if (!answer.confirmed) return;
    // The scope moved while the dialog was open: the operator confirmed a change for a scope it no longer looks at.
    if (currentScope.current !== target) return;
    setBusy(true);
    const res = await runtime.pricingAutomation.setKillSwitch(target, next);
    setBusy(false);
    if (res.ok) {
      setKillSwitch({ view: res.value.killSwitch, unset: false });
      toast({ message: t('dynp.flip.done'), tone: 'success' });
      await load();
      return;
    }
    setError(t('w6.actionRefused', { code: fail(res.error) }));
  };

  const rows = useMemo(() => sortPricingDecisions(decisions ?? []), [decisions]);

  const columns: readonly Column<PricingAutomationDecisionView>[] = [
    { key: 'when', header: t('dynp.col.when'), width: 'minmax(150px, 1.1fr)', render: (d) => <Text role="caption" tone="secondary" mono>{formatMarketInstant(d.at, timeZone, lang, 'datetime-seconds')}</Text> },
    { key: 'decision', header: t('dynp.col.decision'), width: 'minmax(110px, 0.8fr)', render: (d) => <Badge tone={pricingDecisionTone(d)}>{v('pricing_automation.decision', d.decision)}</Badge> },
    { key: 'multiplier', header: t('dynp.col.multiplier'), width: 'minmax(110px, 0.8fr)', render: (d) => <span className="tnum">{multiplierFromBasisPoints(d.appliedMultiplierBp)}×</span> },
    { key: 'reason', header: t('dynp.col.reason'), width: 'minmax(170px, 1.3fr)', render: (d) => v('pricing_automation.reason', d.reason) },
    { key: 'versions', header: t('dynp.col.versions'), width: 'minmax(190px, 1.4fr)', render: (d) => <Text role="caption" tone="secondary">{t('dynp.versions', { policy: d.policyVersion, model: d.modelVersion })}</Text> },
  ];

  return (
    <>
      <PageHeader title={t('dynp.title')} subtitle={t('dynp.subtitle')} />

      {error !== undefined && (
        <div>
          <Notice tone="danger">{error}</Notice>
        </div>
      )}

      <Panel>
        <Row gap={5} align="center" wrap>
          <div className="vello-admin-a-field">
            <StatCard
              label={t('dynp.title')}
              value={killSwitch === undefined ? t(switchUnreadable ? 'dynp.switch.unreadable' : 'ui.loading') : t(killSwitch.view.killed ? 'dynp.switch.killed' : 'dynp.switch.live')}
              hint={killSwitch === undefined ? undefined : t(killSwitch.unset ? 'dynp.switch.unset' : killSwitch.view.killed ? 'dynp.switch.killedDetail' : 'dynp.switch.liveDetail')}
            />
          </div>
          {killSwitch !== undefined && (
            <>
              <Badge tone={pricingKillSwitchTone(killSwitch.view)}>{t(killSwitch.view.killed ? 'dynp.switch.killed' : 'dynp.switch.live')}</Badge>
              {!killSwitch.unset && <Text role="caption" tone="secondary">{t('dynp.switch.revision', { n: String(killSwitch.view.revision) })}</Text>}
              <span className="vello-grow" />
              {mayFlip ? (
                <Button variant={killSwitch.view.killed ? 'primary' : 'danger'} icon={killSwitch.view.killed ? 'rotateCcw' : 'ban'} disabled={busy} onClick={() => void flip()}>
                  {t(killSwitch.view.killed ? 'dynp.revive.action' : 'dynp.kill.action')}
                </Button>
              ) : (
                <Text role="caption" tone="secondary"><span role="note">{t('w6.noPermission.action')}</span></Text>
              )}
            </>
          )}
        </Row>
      </Panel>

      <SectionTitle>{t('dynp.log.title')}</SectionTitle>
      {logError !== undefined && (
        <div>
          <Notice tone="danger">{logError}</Notice>
        </div>
      )}
      <ListTable
        columns={columns}
        rows={rows}
        rowKey={(d) => d.decisionId}
        loading={decisions === undefined && logError === undefined}
        loadingLabel={t('ui.loading')}
        error={logError !== undefined ? t('dynp.switch.unreadable') : undefined}
        onRetry={() => void load()}
        retryLabel={t('ui.retry')}
        empty={<EmptyState title={t('dynp.log.empty.title')} detail={t('dynp.log.empty.detail')} />}
        minWidth={940}
      />
    </>
  );
}
